import React, { useState, useEffect, useMemo } from "react";
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import Dialog from "../../../components/Dialog";
import {
  EMAIL,
  MOBILE_INDIA,
  ONLY_ALPHABETS,
  PAN_INDIA,
  PASSWORD_STRONG,
  testRegex,
} from "../../../config/regex";
import {
  createUser as apiCreateUser,
  updateUser as apiUpdateUser,
} from "../../../api";
import Swal from "sweetalert2";

const validateEmail = (email) => {
  if (!email || !email.trim()) return "Email is required";
  if (!testRegex(EMAIL, email)) return "Enter a valid email address";
  return "";
};

const AddUser = ({ open, onClose, onSuccess, editUser }) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    number: "",
    pan: "",
    experience: "",
    risk: "",
    broker: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(editUser?.id);

  // Helper to get initial values for dirty comparison
  const getInitialValues = useMemo(() => {
    if (!editUser) return null;
    return {
      firstName: editUser.firstName ?? (editUser.name ? editUser.name.split(" ")[0] : ""),
      lastName: editUser.lastName ?? (editUser.name ? editUser.name.split(" ").slice(1).join(" ") : ""),
      email: editUser.email || "",
      number: editUser.number ?? editUser.mobile ?? "",
      pan: editUser.pan ?? "",
      experience: editUser.experience ?? "",
      risk: editUser.risk ?? "",
      broker: editUser.broker ?? "",
    };
  }, [editUser]);

  const isDirty = useMemo(() => {
    if (!isEdit || !getInitialValues) return true;
    return (
      form.firstName.trim() !== getInitialValues.firstName.trim() ||
      form.lastName.trim() !== getInitialValues.lastName.trim() ||
      form.email.trim() !== getInitialValues.email.trim() ||
      form.password !== "" ||
      (form.number || "").trim() !== (getInitialValues.number || "").trim() ||
      (form.pan || "").trim() !== (getInitialValues.pan || "").trim() ||
      (form.experience || "") !== (getInitialValues.experience || "") ||
      (form.risk || "") !== (getInitialValues.risk || "") ||
      (form.broker || "").trim() !== (getInitialValues.broker || "").trim()
    );
  }, [form, getInitialValues, isEdit]);


  const isInvalid = useMemo(() => {
    // 1. Check if any explicit validation errors exist in state
    const hasErrors =
      Object.keys(errors).filter((k) => k !== "submit").length > 0;

    // If editing, we only care if it's valid AND dirty
    if (isEdit) {
      return hasErrors || !isDirty;
    }

    // If adding new user, we need it to be valid
    // hasErrors already captures empty required fields via validate()
    return hasErrors;
  }, [errors, isDirty, isEdit]);

  useEffect(() => {
    if (open && editUser) {
      const initial = {
        firstName: editUser.firstName ?? (editUser.name ? editUser.name.split(" ")[0] : ""),
        lastName: editUser.lastName ?? (editUser.name ? editUser.name.split(" ").slice(1).join(" ") : ""),
        email: editUser.email || "",
        password: "",
        number: editUser.number ?? editUser.mobile ?? "",
        pan: editUser.pan ?? "",
        experience: editUser.experience ?? "",
        risk: editUser.risk ?? "",
        broker: editUser.broker ?? "",
      };
      setForm(initial);
    } else if (open && !editUser) {
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        number: "",
        pan: "",
        experience: "",
        risk: "",
        broker: "",
      });
    }
    if (open) {
      setErrors({});
      setTouched({});
    }
  }, [open, editUser]);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) {
      e.firstName = "First name is required";
    } else if (!testRegex(ONLY_ALPHABETS, form.firstName)) {
      e.firstName = "First name should only contain letters";
    }

    if (!form.lastName.trim()) {
      e.lastName = "Last name is required";
    } else if (!testRegex(ONLY_ALPHABETS, form.lastName)) {
      e.lastName = "Last name should only contain letters";
    }

    const emailErr = validateEmail(form.email);
    if (emailErr) e.email = emailErr;

    if (!isEdit) {
      if (!form.password) {
        e.password = "Password is required";
      } else if (!testRegex(PASSWORD_STRONG, form.password)) {
        e.password =
          "Password must be 8+ chars with uppercase, lowercase, number & special char";
      }
    } else if (form.password && !testRegex(PASSWORD_STRONG, form.password)) {
      e.password =
        "Password must be 8+ chars with uppercase, lowercase, number & special char";
    }

    // Optional fields regex validation
    if (form.number && !testRegex(MOBILE_INDIA, form.number)) {
      e.number = "Enter a valid 10-digit mobile number";
    }
    if (form.pan && !testRegex(PAN_INDIA, form.pan.toUpperCase())) {
      e.pan = "Enter a valid PAN (e.g. ABCDE1234F)";
    }

    setErrors((prev) => ({
      ...e,
      ...(prev.submit ? { submit: prev.submit } : {}),
    }));
    return Object.keys(e).length === 0;
  };

  useEffect(() => {
    validate();
  }, [
    form.firstName,
    form.lastName,
    form.email,
    form.password,
    form.number,
    form.pan,
  ]);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setErrors({});
    try {
      if (isEdit) {
        await apiUpdateUser(editUser.id, {
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
          number: form.number?.trim() ?? "",
          pan: form.pan?.trim() ?? "",
          experience: form.experience?.trim() ?? "",
          risk: form.risk?.trim() ?? "",
          broker: form.broker?.trim() ?? "",
          ...(form.password ? { password: form.password } : {}),
        });
      } else {
        await apiCreateUser({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          number: "",
          email: form.email.trim(),
          password: form.password || "changeme",
        });
      }
      setForm({ firstName: "", lastName: "", email: "", password: "" });
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `User ${isEdit ? "updated" : "created"} successfully`,
        confirmButtonColor: "#38915c",
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Request failed";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#38915c",
      });
      setErrors({ submit: msg });
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        type="submit"
        form="add-user-form"
        disabled={saving || isInvalid}
      >
        {saving ? "Saving..." : isEdit ? "Update" : "Save"}
      </Button>
    </>
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit User" : "Add User"}
      size="lg"
      footer={footer}
    >
      <form
        id="add-user-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <TextInput
          name="firstName"
          label="First name"
          placeholder="First name"
          value={form.firstName}
          onChange={(e) => {
            setForm((f) => ({ ...f, firstName: e.target.value }));
            setTouched((t) => ({ ...t, firstName: true }));
          }}
          onBlur={() => handleBlur("firstName")}
          error={touched.firstName ? errors.firstName : ""}
          required
        />
        <TextInput
          name="lastName"
          label="Last name"
          placeholder="Last name"
          value={form.lastName}
          onChange={(e) => {
            setForm((f) => ({ ...f, lastName: e.target.value }));
            setTouched((t) => ({ ...t, lastName: true }));
          }}
          onBlur={() => handleBlur("lastName")}
          error={touched.lastName ? errors.lastName : ""}
          required
        />
        <TextInput
          name="email"
          label="Email"
          type="email"
          placeholder="email@example.com"
          value={form.email}
          onChange={(e) => {
            setForm((f) => ({ ...f, email: e.target.value }));
            setTouched((t) => ({ ...t, email: true }));
          }}
          onBlur={() => handleBlur("email")}
          error={touched.email ? errors.email : ""}
          required
        />
        <TextInput
          name="password"
          label={isEdit ? "New password (optional)" : "Password"}
          type="password"
          placeholder={isEdit ? "Leave blank to keep" : "Password"}
          value={form.password}
          onChange={(e) => {
            setForm((f) => ({ ...f, password: e.target.value }));
            setTouched((t) => ({ ...t, password: true }));
          }}
          onBlur={() => handleBlur("password")}
          error={touched.password ? errors.password : ""}
          required={!isEdit}
        />
        {isEdit && (
          <>
            <TextInput
              name="number"
              label="Mobile / Number"
              placeholder="Phone number"
              maxLength={10}
              value={form.number}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                setForm((f) => ({ ...f, number: val }));
                setTouched((t) => ({ ...t, number: true }));
              }}
              onBlur={() => handleBlur("number")}
              error={touched.number ? errors.number : ""}
            />
            <TextInput
              name="pan"
              label="PAN"
              placeholder="PAN number"
              maxLength={10}
              value={form.pan}
              onChange={(e) => {
                const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
                setForm((f) => ({ ...f, pan: val }));
                setTouched((t) => ({ ...t, pan: true }));
              }}
              onBlur={() => handleBlur("pan")}
              error={touched.pan ? errors.pan : ""}
            />
            <Dropdown
              name="experience"
              label="Trading experience"
              value={form.experience}
              onChange={(e) => {
                setForm((f) => ({ ...f, experience: e.target.value }));
                setTouched((t) => ({ ...t, experience: true }));
              }}
              options={[
                { value: "Beginner", label: "Beginner" },
                { value: "Intermediate", label: "Intermediate" },
                { value: "Expert", label: "Expert" },
              ]}
            />
            <Dropdown
              name="risk"
              label="Risk appetite"
              value={form.risk}
              onChange={(e) => {
                setForm((f) => ({ ...f, risk: e.target.value }));
                setTouched((t) => ({ ...t, risk: true }));
              }}
              options={[
                { value: "Low", label: "Low" },
                { value: "Medium", label: "Medium" },
                { value: "High", label: "High" },
              ]}
            />
            <TextInput
              name="broker"
              label="Broker name"
              placeholder="Broker"
              value={form.broker}
              onChange={(e) => {
                setForm((f) => ({ ...f, broker: e.target.value }));
                setTouched((t) => ({ ...t, broker: true }));
              }}
              onBlur={() => handleBlur("broker")}
              error={touched.broker ? errors.broker : ""}
            />
          </>
        )}
      </form>
    </Dialog>
  );
};

export default AddUser;
